import type { CompletenessCheck } from "@/types";
import { hasCompletenessSignal } from "../components/document-review/Completeness";

/**
 * Why Approve is (or is not) available on the document-review surface (#7431 AC2).
 *
 * One function so the header's Approve button, its status line, and the sticky
 * "Before you approve" bar (#7431 AC3) can never disagree about the gate or the
 * reason. Causes are checked in the order a reviewer can act on them: access
 * first (nothing else matters if they cannot approve), then the client gate,
 * then running checks, then things that need attention.
 *
 * With the rail (#7432) "attention" is the rail's count of cards still needing
 * attention, passed in as `attention`. Without one (multi-client review) it is
 * the sum of the signals the screen already has: unresolved errored records,
 * loaded-page errors, blocking mapping issues, and a future paid-through date.
 */
export interface ApproveStatusInput {
  /** Caller cannot approve (no documents:create). */
  readOnly: boolean;
  /**
   * The active partner is read-only (ADR-035: AEGL/ACAP or no partner header).
   * Named separately from readOnly because the remedy differs — switch
   * partner, not "ask a reviewer".
   */
  partnerReadOnly?: boolean;
  isClientMissing: boolean;
  /** Multi-client review: every row is excluded or unresolved, so approve has nothing to do. */
  nothingToLoad?: boolean;
  /** Approval forecast / checks still running. */
  isChecking: boolean;
  /** Errors in the loaded records. */
  errorCount: number;
  /** Whole-document unresolved errored records (exclusion-aware). */
  unresolvedErroredCount: number;
  /** Blocking field-mapping issues. */
  mappingBlockingCount: number;
  isAsOfInFuture: boolean;
  /** Optional "X of Y done" progress for the checking line. */
  checksDone?: number;
  checksTotal?: number;
  /**
   * The rail's own count of cards needing attention (#7432). When present it
   * REPLACES the signal arithmetic below: the rail is the one list of checks,
   * and an acknowledged advisory must stop counting here the moment its card
   * becomes a pass line. The signal fields stay for the surfaces without a
   * rail (multi-client review).
   */
  attention?: { count: number; names: string[] };
}

export type ApproveStatusKind =
  | "ready"
  | "view-only"
  | "partner-read-only"
  | "no-client"
  | "nothing-to-load"
  | "checking"
  | "attention";

export interface ApproveStatus {
  kind: ApproveStatusKind;
  /** True when the button must be disabled. */
  blocked: boolean;
  /** The one-line reason shown under / beside the button. Empty when ready. */
  why: string;
  /** Number of items needing attention (0 unless kind === "attention"). */
  attentionCount: number;
  /** Short names of what needs attention, for the sticky bar. */
  attentionNames: string[];
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const deriveApproveStatus = (input: ApproveStatusInput): ApproveStatus => {
  if (input.readOnly) {
    return {
      kind: "view-only",
      blocked: true,
      why: "View only. A reviewer approves this file.",
      attentionCount: 0,
      attentionNames: [],
    };
  }
  if (input.partnerReadOnly) {
    return {
      kind: "partner-read-only",
      blocked: true,
      why: "Read-only partner. Switch to a client partner to approve.",
      attentionCount: 0,
      attentionNames: [],
    };
  }
  if (input.isClientMissing) {
    return {
      kind: "no-client",
      blocked: true,
      why: "Choose a client to approve",
      attentionCount: 0,
      attentionNames: [],
    };
  }
  if (input.nothingToLoad) {
    return {
      kind: "nothing-to-load",
      blocked: true,
      why: "Nothing to load · every row is excluded or unresolved",
      attentionCount: 0,
      attentionNames: [],
    };
  }
  if (input.isChecking) {
    const progress =
      typeof input.checksDone === "number" && typeof input.checksTotal === "number" && input.checksTotal > 0
        ? ` · ${input.checksDone} of ${input.checksTotal} done`
        : "";
    return {
      kind: "checking",
      blocked: true,
      why: `Checking${progress || " · approval forecast running"}`,
      attentionCount: 0,
      attentionNames: [],
    };
  }

  if (input.attention) {
    const { count, names } = input.attention;
    if (count > 0) {
      return {
        kind: "attention",
        blocked: true,
        why: `${count} ${plural(count, "needs", "need")} attention · resolve ${plural(count, "it", "them")} to approve`,
        attentionCount: count,
        attentionNames: names,
      };
    }
    return { kind: "ready", blocked: false, why: "", attentionCount: 0, attentionNames: [] };
  }

  const names: string[] = [];
  // Errored records: the whole-document count already includes the loaded
  // page, so prefer it; fall back to the loaded-page count when the server
  // read is unavailable.
  const erroredCount = Math.max(input.unresolvedErroredCount, input.errorCount);
  if (erroredCount > 0) names.push(`${erroredCount} errored ${plural(erroredCount, "record", "records")}`);
  if (input.mappingBlockingCount > 0) {
    names.push(`${input.mappingBlockingCount} field-mapping ${plural(input.mappingBlockingCount, "issue", "issues")}`);
  }
  if (input.isAsOfInFuture) names.push("paid-through date in the future");

  const attentionCount = erroredCount + input.mappingBlockingCount + (input.isAsOfInFuture ? 1 : 0);
  if (attentionCount > 0) {
    return {
      kind: "attention",
      blocked: true,
      why: `${attentionCount} ${plural(attentionCount, "needs", "need")} attention · resolve ${plural(attentionCount, "it", "them")} to approve`,
      attentionCount,
      attentionNames: names,
    };
  }
  return { kind: "ready", blocked: false, why: "", attentionCount: 0, attentionNames: [] };
};

/**
 * The confirm-before-approve gate (ADR-045 §4 / ADR-051 §8): with no blocking
 * errors, extraction warnings and the soft as-of / double-count / completeness
 * signals route Approve through one confirmation so the reviewer acknowledges
 * every soft signal in one step. ONE derivation, used by the header's Approve
 * and the sticky bar's Approve — the sticky bar once bypassed it (#7431 review).
 */
export interface ApproveConfirmInput {
  hasErrors: boolean;
  warningCount: number;
  asOfWarningMessage?: string;
  doubleCountWarningMessage?: string;
  completeness?: CompletenessCheck;
}

export interface ApproveConfirm {
  needsConfirm: boolean;
  /** Dialog body — composed from whichever soft signals fired. */
  message: string;
}

export const buildApproveConfirm = (input: ApproveConfirmInput): ApproveConfirm => {
  const hasCompletenessWarning = hasCompletenessSignal(input.completeness);
  const needsConfirm =
    !input.hasErrors &&
    (input.warningCount > 0 ||
      !!input.asOfWarningMessage ||
      !!input.doubleCountWarningMessage ||
      hasCompletenessWarning);
  const completenessMessage = hasCompletenessWarning
    ? `Completeness: ${input.completeness?.detail || "the loaded member count could not be verified"}.`
    : null;
  const message =
    [
      input.warningCount > 0
        ? `This document has ${input.warningCount} warning${input.warningCount !== 1 ? "s" : ""} that may indicate extraction issues. ` +
          `Consider adjusting the field mapping to resolve them before proceeding.`
        : null,
      input.asOfWarningMessage || null,
      input.doubleCountWarningMessage || null,
      completenessMessage,
    ]
      .filter(Boolean)
      .join("\n\n") + `\n\nAre you sure you want to approve and load this data?`;
  return { needsConfirm, message };
};
